import { FC, useContext, useState } from 'react';
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { MapCustomControlProps } from '@app/shared/mapping/MapCustomControlProps';
import { MappingContext } from '@app/mapping/MappingContext';
import { createPortal } from 'react-dom';
import { useMapboxControl } from '@app/shared/mapping/useMapBoxControl';
import { NetworkCoverageMapDataContext } from '@app/projects/network-assessment/maps/network-coverage-map/contexts/NetworkCoverageMapDataContext';
import { QESRadioButton } from '@app/shared/qes-standards/forms/RadioButton';
import { ZIP_POINTS_INFO } from '@app/shared/mapping/zipPoints';
import { QesIcon } from '@app/shared/icons/QESIcon';
import { QesIcons } from '@app/shared/icons/qesIcons';
import TooltipWrapper from '@app/shared/TooltipWrapper';

const NetworkCoverageMapLayerControl: FC<MapCustomControlProps> = ({ ignoreInMapExport, imagePath, position }) => {

  const mappingContext = useContext(MappingContext);
  const mapDataContext = useContext(NetworkCoverageMapDataContext);

  const [showLayerControl, setShowLayerControl] = useState(false);

  const handleCheckBoxMemberWithAccesLayerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    mapDataContext.setShowMembersWithAccess(checked);
  }

  const handleCheckBoxMemberWithoutAccessLayerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    mapDataContext.setShowMembersWithoutAccess(checked);
  }

  const handleCheckBoxZipCodeLayerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    mapDataContext.setShowZipCodes(checked);
    if (!checked) mapDataContext.setShowZipPoints(false);
  }

  const handleCheckBoxZipPointsLayerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    mapDataContext.setShowZipPoints(checked);
  }

  const container = useMapboxControl({ mapRef: mapDataContext.mapRef, position });
  if (!container) return null;

  return createPortal(

    <div className="layer-control-main-container" {...(ignoreInMapExport ? { 'data-html2canvas-ignore': '' } : {})}>

      <div className="layer-hover-container" data-testid="network-coverage-map-layer-control-toggle" onClick={() => setShowLayerControl((prev) => !prev)}>
        <img src={imagePath} className="layer-hover-icon" />
      </div>

      {showLayerControl &&
        <div className="layer-control-container">
          {/* Members Section */}
          <span className="provider-member-layer-control__title">
            Members
            {mapDataContext.loadingMembers &&
              <span className="spinner-border spinner-border-sm ml-2" role="status" data-testid="members-loading-spinner">
                <span className="visually-hidden">Loading members...</span>
              </span>
            }
          </span>
          <div className="provider-member-layer-control__layers-container">
            <label className="provider-member-layer-control__label">
              <QESCheckbox
                checked={mapDataContext.showMembersWithAccess}
                onChange={handleCheckBoxMemberWithAccesLayerChange}
                label="With Access"
                name='withAccess'
                testId="members-with-access-checkbox"
              />
            </label>
            <label className="provider-member-layer-control__label">
              <QESCheckbox
                checked={mapDataContext.showMembersWithoutAccess}
                onChange={handleCheckBoxMemberWithoutAccessLayerChange}
                label="Without Access"
                name='withoutAccess'
                testId="members-without-access-checkbox"
              />
            </label>
          </div>

          {/* Map Style Section */}
          <span className="provider-member-layer-control__title">Map Style</span>
          <div className="provider-member-layer-control__layers-container">
            <label className="provider-member-layer-control__label">
              <QESRadioButton
                name="map-style"
                checked={mapDataContext.mapStyle == mappingContext.qesLightStyle}
                onChange={(e) => mapDataContext.setMapStyle(e.target.value)}
                label="Light"
                testId="map-style-light-radio"
                value={mappingContext.qesLightStyle}
              />
            </label>
            <label className="provider-member-layer-control__label">
              <QESRadioButton
                name="map-style"
                checked={mapDataContext.mapStyle == mappingContext.qesOutdoorsStyle}
                onChange={(e) => mapDataContext.setMapStyle(e.target.value)}
                label="Terrain"
                testId="map-style-terrain-radio"
                value={mappingContext.qesOutdoorsStyle}
              />
            </label>
          </div>

          {/* Zip Code Section */}
          <span className="provider-member-layer-control__title">Boundaries</span>
          <div className="provider-member-layer-control__layers-container">
            <label className="provider-member-layer-control__label">
              <QESCheckbox
                checked={mapDataContext.showZipCodes}
                onChange={handleCheckBoxZipCodeLayerChange}
                label="Zip Code"
                name='zipCode'
                testId="map-layer-zipcode-checkbox"
              />
            </label>
            <div className="provider-member-layer-control__nested-row">
              <label className="provider-member-layer-control__label">
                <QESCheckbox
                  checked={mapDataContext.showZipPoints}
                  disabled={!mapDataContext.showZipCodes}
                  onChange={handleCheckBoxZipPointsLayerChange}
                  label="Include Zip Points"
                  name='zipPoints'
                  testId="map-layer-zip-points-checkbox"
                />
              </label>
              <TooltipWrapper text={ZIP_POINTS_INFO} position="left" tooltipWidth="20rem">
                <QesIcon iconName={QesIcons.InfoCircle} color="#828282" height="16px" width="16px" description="About Zip Points" />
              </TooltipWrapper>
            </div>
          </div>
        </div>
      }
    </div>,
    container
  )
};

export default NetworkCoverageMapLayerControl;
